import { useSearchParams } from "react-router-dom";
import { CheckIcon, CrossIcon, FeaturesIcon, SpinnerIcon } from "../Icons";
import {
  procurementAIApi,
  useGetPointsOfInterestQuery,
} from "../../store/apiSlices/procurementAI";
import { InterestsJson } from "../../types/ProcurementChat";
import { toast } from "react-toastify";
import { useRequestLogisticsQuotesMutation } from "../../store/apiSlices/logistics";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";

const OrderRequestKeys: (keyof InterestsJson)[] = [
  "Goods Description",
  "Goods Specifications",
  "Procurement Location",
  "Target Price",
  "Additional Notes",
];

export default function OrderRequestCard() {
  const [searchParams] = useSearchParams();
  const activeThreadId = searchParams.get("threadId");

  const shouldSkip = !activeThreadId || activeThreadId?.startsWith("temp-");

  const { data, isLoading, isFetching } = useGetPointsOfInterestQuery(
    { thread_id: activeThreadId },
    {
      skip: shouldSkip,
    },
  );

  const [requestLogisticsQuotes, { isLoading: isRequestLogisticsLoading }] =
    useRequestLogisticsQuotesMutation();
  const dispatch = useDispatch<AppDispatch>();

  // const interests = data?.interests_json ?? {};
  const interests = shouldSkip ? {} : (data?.interests_json ?? {});

  const isAllFilled =
    !shouldSkip &&
    Object.entries(interests)
      .filter(([key]) => key !== "Additional Notes")
      .every(([, val]) => val !== null && val !== "");

  const handleSubmit = async () => {
    if (!isAllFilled) return;

    const formatted = Object.fromEntries(
      Object.entries(interests).map(([key, value]) => [
        key.toLowerCase().replace(/\s+/g, "_"),
        value,
      ]),
    );

    const payload = {
      booking_medium: "procurement_request",
      thread_id: activeThreadId,
      ...formatted,
    };

    const { data } = await requestLogisticsQuotes(payload);

    if (data.status === "success") {
      toast.success("Submission successful.");

      dispatch(
        procurementAIApi.util.updateQueryData(
          "getPointsOfInterest",
          { thread_id: activeThreadId }, // must match the original query arg
          (draft) => {
            if (draft) {
              draft.is_submitted = 1;
            }
          },
        ),
      );
    } else {
      toast.error("Something went wrong please try again!");
    }
  };

  if (isLoading || isFetching) return <OrderRequestSkeleton />;

  return (
    <div className="p-4 lg:p-5 border border-[#F3F3F5] rounded-[10px]">
      <h3 className="mb-4 font-semibold text-base lg:text-[18px] inline-flex items-center gap-2">
        <FeaturesIcon className="size-6" /> Order Request
      </h3>

      <div className="space-y-[10px] mt-3">
        {OrderRequestKeys.map((key) => {
          const value = interests?.[key as keyof typeof interests];
          const isFilled =
            value !== null && value !== undefined && value !== "";

          return (
            <p key={key} className="flex items-center gap-2 text-sm">
              {isFilled ? (
                <CheckIcon className="size-4 text-custom-blue-medium shrink-0" />
              ) : (
                <CrossIcon className="size-4 flex items-center justify-center text-red-500 shrink-0" />
              )}

              <span className="text-[#475467]">
                {key}: {value}
              </span>
            </p>
          );
        })}
      </div>

      {data?.is_submitted ? (
        <div className="mt-4 p-4 rounded-md bg-green-50 border border-green-200 flex items-start gap-3">
          <div>
            <CheckIcon className="size-4 text-green-600 mt-2" />
          </div>

          <div>
            <p className="font-semibold text-green-700">Request Submitted</p>
            <p className="text-sm text-green-600">
              Your request has been successfully submitted. We'll get back to
              you soon.
            </p>
          </div>
        </div>
      ) : (
        <button
          disabled={!isAllFilled || isRequestLogisticsLoading}
          onClick={handleSubmit}
          className="w-full h-[44px] rounded-md font-medium mt-4
               bg-custom-blue-medium text-white
               disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed
               flex items-center justify-center gap-2"
        >
          {isRequestLogisticsLoading && (
            <SpinnerIcon className="size-4 animate-spin fill-blue-600" />
          )}

          {isRequestLogisticsLoading ? "Submitting..." : "Submit Request"}
        </button>
      )}
    </div>
  );
}

function OrderRequestSkeleton() {
  return (
    <div className="p-5 border border-[#F3F3F5] rounded-[10px] animate-pulse">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="size-6 bg-gray-200 rounded" />
        <div className="h-5 w-32 bg-gray-200 rounded" />
      </div>

      {/* List */}
      <div className="space-y-[12px] mt-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-start gap-3">
            {/* Icon placeholder */}
            <div className="size-4 bg-gray-200 rounded-full shrink-0 mt-[3px]" />

            {/* Text */}
            <div className="flex flex-col gap-2 w-full">
              <div className="h-3 w-40 bg-gray-200 rounded" />
              {/* <div className="h-3 w-60 bg-gray-100 rounded" /> */}
            </div>
          </div>
        ))}
      </div>

      {/* Button */}
      <div className="h-[44px] w-full bg-gray-200 rounded-md mt-4" />
    </div>
  );
}
